/**
 * S11.8.4 + S11.11.3 — quyết định route công khai ở mức pathname (Plan Task 4, Spec §6/§7).
 *
 * Hàm ở đây là ranh giới AN NINH của entrypoint: nó quyết định lượt tải trang nào được bỏ qua
 * Keycloak/OIDC. Vì vậy matcher phải khớp ĐÚNG từng route và không được nới rộng — stop condition
 * Plan §7 "auth bypass match rộng hơn exact route". Mọi dạng gần giống (segment thừa, phiên bản,
 * trailing slash, query/hash, slug rỗng hay slug chứa dấu chấm/percent) đều phải rơi về nhánh xác
 * thực hiện hành, kể cả khi URL công khai hoàn toàn là dữ liệu synthetic.
 *
 * Tập công khai nay có BA mẫu neo tuyệt đối, khai riêng từng hằng ở dưới: cổng `/public`, sơ đồ số
 * `/public/cemeteries/:slug/3d`, và hồ sơ công khai `/public/martyrs/<khoá>` (mã minh họa
 * `demo-NNNN` HOẶC mã liệt sĩ thật `LS-NNNNNN`). Không có tiền tố chung nào được bỏ qua xác thực:
 * `/public` là mẫu ĐỘC LẬP với hai mẫu còn lại, nên mọi path con của nó vẫn phải qua Keycloak nếu
 * không khớp một trong hai mẫu kia.
 */

/**
 * Route công khai scene 3D: `/public/cemeteries/<slug>/3d`.
 *
 * - Slug đúng MỘT segment, chỉ gồm chữ cái, chữ số, `_` và `-` (không dấu chấm, không `%`, không
 *   `/`, nên `..` và `%2e%2e` tự động bị loại).
 * - Neo `^`/`$` nên segment thừa, trailing slash, query và hash đều không khớp.
 * - Phân biệt hoa/thường và chỉ nhận `/3d` viết thường.
 *
 * Cố ý KHÔNG dùng cờ `g`: regex có trạng thái `lastIndex` sẽ trả sai kết quả khi gọi lặp lại.
 */
const PUBLIC_CEMETERY_3D_PATH = /^\/public\/cemeteries\/[A-Za-z0-9_-]+\/3d$/;

/**
 * Route hồ sơ công khai: `/public/martyrs/<khoá>` — HAI định dạng khoá, cả hai đều hợp lệ.
 *
 * - `demo-<4 chữ số>` — mã MINH HỌA của S11.9.5, cùng allowlist mà layout synthetic sinh ra
 *   (`/public/martyrs/demo-[0-9]{4}`). Nhiều fixture và ảnh evidence cũ đang dùng; **không** được
 *   thu hẹp.
 * - `LS-<từ 6 chữ số>` — mã liệt sĩ THẬT (`martyr.profile.martyr_code`, S12.4 §8). Sàn 6 chữ số
 *   giữ nguyên; TRẦN thì cố ý không chặn: cột là `varchar(60)` và seed sinh mã tự động, nên một mã
 *   7 chữ số trong tương lai rơi vào nhánh OIDC sẽ **hỏng im lặng** — đúng loại lỗi mà mẫu này
 *   dựng ra để chặn.
 *
 * Neo `^`/`$` buộc khớp trọn chuỗi, nên segment thừa, trailing slash, query/hash, percent-encoding,
 * `LS-` viết thường hay một `-` thứ hai đều KHÔNG lọt vào nhánh không xác thực.
 */
const PUBLIC_PROFILE_PATH = /^\/public\/martyrs\/(?:demo-[0-9]{4}|LS-[0-9]{6,})$/;

/**
 * Đường dẫn cổng công khai, ở dạng trần — nguồn sự thật cho ĐÍCH ĐĂNG XUẤT.
 *
 * `auth/config.ts` ghép hằng này vào origin để dựng `postLogoutRedirectUri`.
 * Người dùng khi truy cập vào app bình thường sẽ vào `/`, và khi đăng xuất cũng chuyển về `/`.
 */
export const PUBLIC_PORTAL_HOME = "/";

/**
 * Cổng công khai: `/` (và hỗ trợ `/public` chuyển tiếp).
 *
 * Mẫu này là mẫu NEO TUYỆT ĐỐI, độc lập với hai mẫu trên: `^`/`$` buộc khớp trọn chuỗi.
 */
const PUBLIC_PORTAL_HOME_PATH = /^(?:\/|\/public)$/;

/**
 * Route công khai tra cứu / danh sách liệt sĩ: `/danh-sach-liet-si` và `/tra-cuu`.
 */
const PUBLIC_MARTYRS_SEARCH_PATH = /^\/(?:danh-sach-liet-si|tra-cuu)$/;

/**
 * `true` khi vị trí hiện tại mang tham số callback xác thực OIDC
 * (trong search query hoặc hash fragment, ví dụ `?state=...&code=...` hoặc `#state=...&code=...`).
 *
 * Dù pathname là `/`, nếu mang tham số này thì đây là lượt redirect trở về sau đăng nhập OIDC,
 * entrypoint phải chạy nhánh xác thực để xử lý token thay vì coi là trang cổng công khai thông thường.
 */
export function isOidcCallbackLocation(
  location: Partial<Pick<Location, "search" | "hash">>,
): boolean {
  if (location.search) {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.has("state") && (searchParams.has("code") || searchParams.has("error"))) {
      return true;
    }
  }
  if (location.hash) {
    const hashParams = new URLSearchParams(location.hash.replace(/^#/, ""));
    if (hashParams.has("state") && (hashParams.has("code") || hashParams.has("error"))) {
      return true;
    }
  }
  return false;
}

/**
 * `true` chỉ khi `pathname` là đúng route công khai `/public/cemeteries/:slug/3d`.
 *
 * @param pathname đường dẫn tuyệt đối lấy từ `window.location.pathname` (đã tách query/hash).
 * @returns `true` nếu lượt tải này được phép chạy không OIDC; mọi trường hợp khác trả `false`.
 */
export function isPublicCemetery3dPath(pathname: string): boolean {
  return PUBLIC_CEMETERY_3D_PATH.test(pathname);
}

/**
 * `true` khi `pathname` thuộc BẤT KỲ route công khai nào của S11.8/S11.9/S11.11/S12.4.
 *
 * Entrypoint dùng hàm này để chọn nhánh không-OIDC. Tập route vẫn đóng: chỉ các mẫu neo tuyệt đối
 * ở trên, không có tiền tố chung nào được bỏ qua xác thực.
 *
 * @param pathname đường dẫn tuyệt đối từ `window.location.pathname`
 * @returns `true` nếu thuộc route công khai đã duyệt
 */
export function isPublicPath(pathname: string): boolean {
  return (
    PUBLIC_PORTAL_HOME_PATH.test(pathname) ||
    PUBLIC_CEMETERY_3D_PATH.test(pathname) ||
    PUBLIC_PROFILE_PATH.test(pathname) ||
    PUBLIC_MARTYRS_SEARCH_PATH.test(pathname)
  );
}
